<script>
  /**
   * Una fila de la lista de movimientos.
   *
   * Decisiones de lectura:
   *  · El gasto va en tinta neutra, no en rojo. Casi todo son gastos; pintarlos
   *    todos de rojo convierte la lista en un muro de alarma y le quita el
   *    significado al rojo cuando de verdad hace falta.
   *  · El ingreso va en verde azulado y con "+". El signo importa: no todo el
   *    mundo distingue verde de rojo, así que el color nunca es la única señal.
   *  · El traslado va en índigo y con "→", porque no es ni ingreso ni gasto.
   *  · El punto de "pendiente de subir" es gris, nunca rojo: no es un error,
   *    es lo normal cuando no hay señal.
   */
  import { formatear, formatearAria } from '../lib/dinero.js';
  import { TIPO_MOVIMIENTO } from '../lib/db.js';

  let { movimiento, categoria = null, cuenta = null, pendiente = false } = $props();

  const esIngreso = $derived(movimiento.tipo === TIPO_MOVIMIENTO.INGRESO);
  const esTraslado = $derived(movimiento.tipo === TIPO_MOVIMIENTO.TRANSFERENCIA);

  const signo = $derived(esIngreso ? '+' : esTraslado ? '' : '−');

  const titulo = $derived(
    movimiento.concepto?.trim() ||
      movimiento.contraparte?.trim() ||
      categoria?.nombre ||
      (esTraslado ? 'Traslado' : 'Sin concepto')
  );

  const hora = $derived(movimiento.hora?.slice(0, 5) ?? '');

  /**
   * Los datos secundarios, ya filtrados.
   * Se arma como lista y se intercalan los separadores al recorrerla, porque
   * poniendo el "·" dentro de cada bloque aparece uno suelto al principio
   * cuando el primer dato falta — por ejemplo, un gasto sin categoría.
   */
  const meta = $derived(
    [categoria?.nombre, cuenta?.nombre, hora].filter(Boolean)
  );
</script>

<article class="fila" data-tipo={movimiento.tipo}>
  <div class="icono" aria-hidden="true">
    {#if esTraslado}
      →
    {:else}
      {cuenta?.icono ?? (esIngreso ? '↓' : '↑')}
    {/if}
  </div>

  <div class="texto">
    <p class="titulo">{titulo}</p>
    {#if meta.length}
      <p class="meta">
        {#each meta as dato, i}
          {#if i > 0}<span class="punto" aria-hidden="true">·</span>{/if}
          <span>{dato}</span>
        {/each}
      </p>
    {/if}
  </div>

  <div class="derecha">
    <span class="monto cifra" aria-hidden="true">
      {signo}{formatear(movimiento.monto, { simbolo: false })}
    </span>
    <span class="solo-lectores">
      {esIngreso ? 'ingreso de' : esTraslado ? 'traslado de' : 'gasto de'}
      {formatearAria(movimiento.monto)}
    </span>

    {#if pendiente}
      <span class="pendiente" title="Guardado aquí, falta subirlo">
        <span class="bolita" aria-hidden="true"></span>
        <span class="solo-lectores">pendiente de subir</span>
      </span>
    {/if}

    {#if movimiento.origen_dato === 'ocr' && !movimiento.revisado}
      <span class="revisar">revisar</span>
    {/if}
  </div>
</article>

<style>
  .fila {
    display: grid;
    grid-template-columns: 40px 1fr auto;
    gap: var(--e-3);
    align-items: center;
    padding: var(--e-3) var(--gutter);
    min-height: 64px;
    border-bottom: 1px solid var(--border);
  }

  .icono {
    width: 40px;
    height: 40px;
    display: grid;
    place-items: center;
    border-radius: var(--r-full);
    background: var(--bg-sunken);
    font-size: var(--t-lg);
  }
  .fila[data-tipo='ingreso'] .icono {
    background: var(--ingreso-suave);
    color: var(--ingreso);
  }
  .fila[data-tipo='transferencia'] .icono {
    background: var(--transferencia-suave);
    color: var(--transferencia);
  }

  .texto { min-width: 0 }

  .titulo {
    font-size: var(--t-base);
    font-weight: var(--w-medium);
    color: var(--ink-1);
    /* Un concepto largo no puede empujar el monto fuera de la pantalla. */
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .meta {
    font-size: var(--t-xs);
    color: var(--ink-3);
    display: flex;
    gap: var(--e-1);
    align-items: center;
    overflow: hidden;
    white-space: nowrap;
  }
  .punto { opacity: 0.5 }

  .derecha {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;
  }

  .monto {
    font-size: var(--t-base);
    font-weight: var(--w-semibold);
    /* Neutro por defecto: el gasto es la regla, no la excepción. */
    color: var(--ink-1);
    white-space: nowrap;
  }
  .fila[data-tipo='ingreso'] .monto       { color: var(--ingreso) }
  .fila[data-tipo='transferencia'] .monto { color: var(--transferencia) }

  .pendiente { display: flex; align-items: center }
  .bolita {
    width: 6px;
    height: 6px;
    border-radius: var(--r-full);
    /* Gris, no rojo. Estar sin señal no es un fallo. */
    background: var(--sync-pendiente);
    display: block;
  }

  .revisar {
    font-size: var(--t-2xs);
    font-weight: var(--w-semibold);
    text-transform: uppercase;
    letter-spacing: var(--track-wide);
    color: var(--alerta);
    background: var(--alerta-suave);
    padding: 1px var(--e-1);
    border-radius: var(--r-xs);
  }
</style>
